import type { CSSProperties } from 'react';
import type { AppointmentWhatsAppResponseClientRow } from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  AppointmentResponseBadge (PRD.224)                                */
/*                                                                    */
/*  Renders the patient's WhatsApp reply state for an appointment.    */
/*  Shows nothing when there's no response yet (don't clutter the     */
/*  row); a small coloured pill when the patient confirmed or         */
/*  requested cancellation.                                           */
/* ------------------------------------------------------------------ */

export interface AppointmentResponseBadgeProps {
  /** Latest matched response for this appointment, or null. */
  response: AppointmentWhatsAppResponseClientRow | null;
  /** Visual size — defaults to "sm". */
  size?: 'sm' | 'md';
}

export function AppointmentResponseBadge({ response, size = 'sm' }: AppointmentResponseBadgeProps) {
  if (!response || !response.response_intent) return null;

  const intent = response.response_intent;
  const isConfirmed = intent === 'confirmed';
  const isCancel = intent === 'cancellation_requested';
  if (!isConfirmed && !isCancel) return null;

  const palette = isConfirmed
    ? { bg: '#dcfce7', fg: '#15803d', label: 'Patient confirmed' }
    : { bg: '#fef3c7', fg: '#92400e', label: 'Cancellation requested' };

  return (
    <span
      title={tooltipFor(response)}
      style={{
        ...badgeBaseStyle,
        ...sizeStyle(size),
        background: palette.bg,
        color: palette.fg,
      }}
    >
      <DotIcon color={palette.fg} />
      {palette.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function tooltipFor(response: AppointmentWhatsAppResponseClientRow): string {
  const when = response.responded_at
    ? new Date(response.responded_at).toLocaleString()
    : 'recently';
  const via = response.match_strategy === 'reply_context' ? 'reply to reminder' : 'phone match';
  const text = response.response_text ? ` — "${response.response_text}"` : '';
  return `Replied via WhatsApp (${via}) ${when}${text}`;
}

function DotIcon({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

/**
 * Reduce an unsorted list of responses to the latest one per appointment id.
 * Inputs are expected to come from `qAppointmentWhatsappResponsesByAppointmentIds`
 * which already orders by responded_at desc — but we recompute defensively.
 */
export function indexLatestResponseByAppointmentId(
  rows: AppointmentWhatsAppResponseClientRow[],
): Map<string, AppointmentWhatsAppResponseClientRow> {
  const out = new Map<string, AppointmentWhatsAppResponseClientRow>();
  for (const row of rows) {
    const id = row.appointment_id;
    if (!id) continue;
    const existing = out.get(id);
    if (!existing) {
      out.set(id, row);
      continue;
    }
    const a = row.responded_at ?? '';
    const b = existing.responded_at ?? '';
    if (a > b) out.set(id, row);
  }
  return out;
}

const badgeBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 'var(--radius-full)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

function sizeStyle(size: 'sm' | 'md'): CSSProperties {
  return size === 'md'
    ? { fontSize: 'var(--font-size-sm)', padding: '4px 10px' }
    : { fontSize: 'var(--font-size-xs)', padding: '3px 8px' };
}
